import { useState, useEffect, useMemo } from 'react';
import { Lock, AlertCircle } from 'lucide-react';
import { StatusBadge } from '../common';
import { calculateTotalEarnings, calculatePendingEarnings } from '../../services/commissionService';
import { STATUS_META } from '../../utils/constants';
import { supabase } from '../../services/supabaseClient';

/**
 * Rep (Field Representative) Dashboard
 * Shows today's visits, deal stats, recent activity, and earnings
 */
export function RepDashboard({ currentUser, restaurants, userCommissions }) {
  const [todayVisits, setTodayVisits] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [showPendingTopupInfo, setShowPendingTopupInfo] = useState(false);
  const [freeRegistrationsRemaining, setFreeRegistrationsRemaining] = useState(null);

  // Load visit and activity data
  useEffect(() => {
    if (!currentUser || !restaurants) return;

    const today = new Date().toISOString().split('T')[0];
    
    // Filter rep's deals
    const myDeals = restaurants.filter(r => r.assignedRep === currentUser.id);
    
    // Today's visits
    const visitsToday = myDeals.filter(r => r.visitDate === today);
    setTodayVisits(visitsToday);

    // Recent 5 deals (sorted by updated date)
    const sorted = [...myDeals]
      .filter(r => r.updated_at || r.created_at)
      .sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at))
      .slice(0, 5);
    setRecentActivity(sorted);

    // Load free registrations remaining
    const loadFreeRegistrations = async () => {
      try {
        const { data, error } = await supabase
          .from('users')
          .select('free_registrations_remaining')
          .eq('id', currentUser.id)
          .single();
        
        if (error) throw error;
        setFreeRegistrationsRemaining(data?.free_registrations_remaining ?? 0);
      } catch (err) {
        console.error('Load free registrations error:', err);
      }
    };

    loadFreeRegistrations();
  }, [currentUser, restaurants]);

  // Calculate stats
  const myDeals = useMemo(() => 
    restaurants?.filter(r => r.assignedRep === currentUser.id) || [],
    [restaurants, currentUser]
  );

  const today = new Date().toISOString().split('T')[0];
  const visitsToday = myDeals.filter(r => r.visitDate === today).length;
  const closedDeals = myDeals.filter(r => r.status === 'deal_closed').length;
  
  // Pending top-up deals for rep
  const pendingTopupDeals = useMemo(() => 
    myDeals.filter(r => r.status === 'deal_closed' && r.commissionPending === true),
    [myDeals]
  );
  const pendingTopupCount = pendingTopupDeals.length;
  const potentialPendingEarnings = pendingTopupCount * 35; // 35 AED per deal for rep
  
  const totalEarnings = calculateTotalEarnings(userCommissions || []);
  const pendingEarnings = calculatePendingEarnings(userCommissions || []);

  return (
    <div style={{ padding: 'clamp(16px, 3vw, 32px)' }}>
      {/* Header */}
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ color: '#000000', fontSize: 28, fontWeight: 900, marginBottom: 8 }}>
          🚶 لوحة التحكم
        </h1>
        <p style={{ color: '#000000', fontSize: 14 }}>
          مرحباً {currentUser.name}، إليك جدول زياراتك وأدائك
        </p>
      </div>

      {/* Today's Visits */}
      {todayVisits.length > 0 && (
        <div style={{
          background: 'linear-gradient(135deg, #ffffff, #ffffff)',
          borderRadius: 16,
          padding: 24,
          marginBottom: 24,
          border: '2px solid #22d3ee',
        }}>
          <h3 style={{ color: '#000000', fontSize: 18, fontWeight: 900, margin: '0 0 16px' }}>
            📅 زيارات اليوم ({todayVisits.length})
          </h3>

          <div style={{ display: 'grid', gap: 12 }}>
            {todayVisits.map(visit => (
              <div key={visit.id} style={{
                background: '#ffffff',
                borderRadius: 12,
                padding: 16,
                border: '1px solid #e2e8f0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 12,
              }}>
                <div>
                  <div style={{ color: '#000000', fontWeight: 700, fontSize: 15, marginBottom: 4 }}>
                    {visit.name}
                  </div>
                  <div style={{ color: '#000000', fontSize: 13 }}>
                    {visit.area || 'بدون منطقة'} • {visit.phone || 'بدون هاتف'}
                  </div>
                </div>
                <StatusBadge status={visit.status} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Free Registrations Card */}
      {freeRegistrationsRemaining !== null && (
        <div style={{
          background: freeRegistrationsRemaining > 0 
            ? 'linear-gradient(135deg, #10b98122, #05966911)' 
            : 'linear-gradient(135deg, #ef444422, #dc262611)',
          borderRadius: 16,
          padding: 24,
          marginBottom: 24,
          border: `2px solid ${freeRegistrationsRemaining > 0 ? '#10b981' : '#ef4444'}`,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              background: freeRegistrationsRemaining > 0 ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 32,
            }}>
              {freeRegistrationsRemaining > 0 ? '🎁' : '⚠️'}
            </div>
            <div style={{ flex: 1 }}>
              <h3 style={{ 
                color: freeRegistrationsRemaining > 0 ? '#10b981' : '#ef4444', 
                fontSize: 20, 
                fontWeight: 900, 
                margin: '0 0 8px' 
              }}>
                {freeRegistrationsRemaining > 0 
                  ? `التسجيلات المجانية: ${freeRegistrationsRemaining} متبقي` 
                  : 'نفذت التسجيلات المجانية!'}
              </h3>
              <p style={{ color: '#000000', fontSize: 14, margin: 0 }}>
                {freeRegistrationsRemaining > 0
                  ? `لديك ${freeRegistrationsRemaining} مطاعم يمكنك تسجيلها مجاناً (العميل لا يدفع)`
                  : 'يجب إقناع المطاعم بالدفع (150 أو 300 AED) - لا يمكنك التسجيل مجاناً'}
              </p>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ 
                color: freeRegistrationsRemaining > 0 ? '#10b981' : '#ef4444',
                fontSize: 48,
                fontWeight: 900,
                lineHeight: 1,
              }}>
                {freeRegistrationsRemaining}
              </div>
              <div style={{ color: '#000000', fontSize: 12, marginTop: 4 }}>
                من 5
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pending Top-up Deals Container */}
      {pendingTopupCount > 0 && (
        <div style={{
          background: 'linear-gradient(135deg, #ffffff, #0f172a)',
          borderRadius: 16,
          padding: 24,
          marginBottom: 32,
          border: '2px solid #f59e0b',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{
                width: 48,
                height: 48,
                borderRadius: 12,
                background: 'rgba(245, 158, 11, 0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <Lock size={24} color="#fbbf24" />
              </div>
              <div>
                <h3 style={{ color: '#000000', fontSize: 18, fontWeight: 900, margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                  صفقات بانتظار الشحن الأول
                  <button
                    onClick={() => setShowPendingTopupInfo(!showPendingTopupInfo)}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      padding: 0,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                    title="شرح"
                  >
                    <AlertCircle size={18} color="#fbbf24" />
                  </button>
                </h3>
                <p style={{ color: '#000000', fontSize: 13, margin: '4px 0 0' }}>
                  {pendingTopupCount} {pendingTopupCount === 1 ? 'صفقة' : 'صفقة'} مغلقة بانتظار شحن المطعم
                </p>
              </div>
            </div>
            <div style={{ textAlign: 'left' }}>
              <div style={{ color: '#64748b', fontSize: 12 }}>العمولة المتوقعة</div>
              <div style={{ color: '#fbbf24', fontSize: 28, fontWeight: 900 }}>
                {potentialPendingEarnings} AED
              </div>
            </div>
          </div>

          {/* Info Popup */}
          {showPendingTopupInfo && (
            <div style={{
              padding: 16,
              background: 'rgba(245, 158, 11, 0.1)',
              borderRadius: 12,
              border: '1px solid #f59e0b',
              marginBottom: 16,
            }}>
              <h4 style={{ color: '#fbbf24', fontSize: 14, fontWeight: 700, marginBottom: 8 }}>
                ℹ️ لماذا هذه الصفقات معلقة؟
              </h4>
              <ul style={{ color: '#000000', fontSize: 13, lineHeight: 1.8, margin: 0, paddingRight: 20 }}>
                <li>اختر المطعم خيار "الدفع بعد الشحن الأول"</li>
                <li>لن يتم صرف العمولة (35 AED) حتى يشحن المطعم محفظته</li>
                <li>بمجرد الشحن الأول، ستتحول العمولة إلى "مستحقة"</li>
                <li>يمكنك حث المطعم على الشحن أثناء زيارتك القادمة</li>
              </ul>
            </div>
          )}

          {/* Pending Deals List */}
          <div style={{ display: 'grid', gap: 8 }}>
            {pendingTopupDeals.slice(0, 5).map(deal => (
              <div
                key={deal.id}
                style={{
                  padding: 12,
                  background: 'rgba(245, 158, 11, 0.05)',
                  borderRadius: 10,
                  border: '1px solid rgba(245, 158, 11, 0.3)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <Lock size={16} color="#fbbf24" />
                  <div>
                    <div style={{ color: '#000000', fontSize: 14, fontWeight: 700 }}>
                      {deal.name}
                    </div>
                    <div style={{ color: '#64748b', fontSize: 11 }}>
                      {deal.area || '—'}
                    </div>
                  </div>
                </div>
                <div style={{ color: '#fbbf24', fontSize: 13, fontWeight: 700 }}>
                  35 AED
                </div>
              </div>
            ))}
            {pendingTopupCount > 5 && (
              <div style={{ textAlign: 'center', color: '#64748b', fontSize: 12, paddingTop: 8 }}>
                و {pendingTopupCount - 5} صفقات أخرى...
              </div>
            )}
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
        gap: 16, 
        marginBottom: 32 
      }}>
        <div style={{
          background: 'linear-gradient(135deg, #ffffff, #ffffff)',
          border: '1px solid #22d3ee',
          borderRadius: 16,
          padding: 24,
        }}>
          <p style={{ color: '#67e8f9', fontSize: 12, marginBottom: 8 }}>زيارات اليوم</p>
          <p style={{ color: '#22d3ee', fontSize: 32, fontWeight: 900 }}>{visitsToday}</p>
        </div>

        <div style={{
          background: 'linear-gradient(135deg, #ffffff, #ffffff)',
          border: '1px solid #10b981',
          borderRadius: 16,
          padding: 24,
        }}>
          <p style={{ color: '#6ee7b7', fontSize: 12, marginBottom: 8 }}>صفقات مغلقة</p>
          <p style={{ color: '#34d399', fontSize: 32, fontWeight: 900 }}>{closedDeals}</p>
        </div>

        <div style={{
          background: 'linear-gradient(135deg, #ffffff, #ffffff)',
          border: '1px solid #10b981',
          borderRadius: 16,
          padding: 24,
        }}>
          <p style={{ color: '#6ee7b7', fontSize: 12, marginBottom: 8 }}>إجمالي الأرباح</p>
          <p style={{ color: '#34d399', fontSize: 32, fontWeight: 900 }}>{totalEarnings.toFixed(0)} AED</p>
        </div>

        <div style={{
          background: 'linear-gradient(135deg, #ffffff, #ffffff)',
          border: '1px solid #f59e0b',
          borderRadius: 16,
          padding: 24,
        }}>
          <p style={{ color: '#fcd34d', fontSize: 12, marginBottom: 8 }}>في الانتظار</p>
          <p style={{ color: '#f59e0b', fontSize: 32, fontWeight: 900 }}>{pendingEarnings.toFixed(0)} AED</p>
        </div>
      </div>

      {/* Recent Activity */}
      {recentActivity.length > 0 && (
        <div style={{
          background: '#ffffff',
          borderRadius: 16,
          border: '1px solid #e2e8f0',
          overflow: 'hidden',
        }}>
          <div style={{ padding: 20, borderBottom: '1px solid #e2e8f0' }}>
            <h2 style={{ color: '#000000', fontSize: 18, fontWeight: 700, margin: 0 }}>
              آخر النشاطات
            </h2>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#ffffff' }}>
                  <th style={{ padding: '12px 16px', color: '#3d5470', fontSize: 11, fontWeight: 700, textAlign: 'right' }}>المطعم</th>
                  <th style={{ padding: '12px 16px', color: '#3d5470', fontSize: 11, fontWeight: 700, textAlign: 'right' }}>تاريخ الزيارة</th>
                  <th style={{ padding: '12px 16px', color: '#3d5470', fontSize: 11, fontWeight: 700, textAlign: 'right' }}>الحالة</th>
                </tr>
              </thead>
              <tbody>
                {recentActivity.map(deal => (
                  <tr key={deal.id} style={{ borderTop: '1px solid #e2e8f0' }}>
                    <td style={{ padding: '12px 16px', color: '#000000', fontWeight: 700 }}>{deal.name}</td>
                    <td style={{ padding: '12px 16px', color: '#000000', fontSize: 13 }}>
                      {deal.visitDate ? new Date(deal.visitDate).toLocaleDateString('ar-AE') : '-'}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <StatusBadge status={deal.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* No Visits Today */}
      {todayVisits.length === 0 && (
        <div style={{
          background: '#ffffff',
          borderRadius: 16,
          border: '1px solid #e2e8f0',
          padding: 48,
          textAlign: 'center',
        }}>
          <p style={{ fontSize: 48, marginBottom: 16 }}>📅</p>
          <p style={{ color: '#000000', fontSize: 16, fontWeight: 700, marginBottom: 8 }}>
            لا توجد زيارات مجدولة اليوم
          </p>
          <p style={{ color: '#64748b', fontSize: 13 }}>
            سيتم عرض الزيارات هنا عندما يقوم المدير بجدولتها
          </p>
        </div>
      )}
    </div>
  );
}
